import React from 'react';
import ReactDOM from 'react-dom/client';

/*
 * Global CSS BEFORE `App` (PBI #1144 cascade contract)
 * ----------------------------------------------------
 * Vite emits CSS in JS import-graph order. If `App` is imported first,
 * every CSS module in the App tree (AppModal.module.css, phone input,
 * landing modules, …) lands BEFORE `@mantine/core/styles.css`. Mantine's
 * late Paper `display: block`, Input, and Modal rules then win the
 * cascade — sticky footers flicker, and any module that restyles a
 * Mantine surface silently loses.
 *
 * Pre-#1144, `@mantine/core/styles.css` sat near the top of App.tsx
 * (before most component imports), so modules won. Keep that semantics
 * here: monolith → AppModal shell → fonts → app globals → then App.
 */
import './styles/mantineEntryStyles';
// MUST follow mantineEntryStyles — restores AppModal 100svh / flex shell.
import './styles/appModalViewportShell.css';
import './styles/brandFonts.css';
// Form-validation `[data-error]` rules must follow the Mantine monolith
// (same specificity; later source wins). See style.css "Form validation".
import './style.css';

import App from './App';
import { AppErrorBoundary } from './components/common/AppErrorBoundary';
import {
  captureAcquisitionAttributionFromLocation,
  restoreAcquisitionAttributionFromDurableIfNeeded,
} from './services/acquisitionAttributionService';
import { registerVitePreloadErrorReload } from './utils/staleDeployChunkReload';
import { isProductionHostname } from './utils/productionHost';

/**
 * Mobile debug console (Eruda) loader.
 *
 * Only available in non-production environments (localhost, dev.scheduledsmiles.com).
 * Activate by navigating to the site with ?eruda in the URL, e.g.:
 *   https://dev.scheduledsmiles.com?eruda
 *
 * Once activated, the preference is saved in localStorage so Eruda loads
 * automatically on subsequent visits. To disable, navigate with ?eruda=off
 * or clear localStorage.
 */
function loadEruda(): void {

  // Never load in production
  if (isProductionHostname()) return;

  const params = new URLSearchParams(window.location.search);
  const STORAGE_KEY = 'eruda-enabled';

  // ?eruda=off disables and clears the preference
  if (params.get('eruda') === 'off') {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  // ?eruda (any value or no value) enables and persists
  if (params.has('eruda')) {
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  // Load if enabled via URL or persisted preference
  if (params.has('eruda') || localStorage.getItem(STORAGE_KEY) === 'true') {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/eruda';
    script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).eruda?.init();
    };
    document.head.appendChild(script);
  }
}

loadEruda();

// After a SWA deploy, stale tabs may fail Vite CSS/JS preloads for old hashes.
// Reload once (cooldown-guarded) so users pick up the new index.html assets.
registerVitePreloadErrorReload();

// Capture ad click IDs / UTMs before React/MSAL so AuthStartPage URL stripping
// and the Entra round-trip cannot lose first-touch attribution (PBI #1104).
// PBI #1500: restore Marketing-gated durable first-touch once per load, then capture.
restoreAcquisitionAttributionFromDurableIfNeeded();
captureAcquisitionAttributionFromLocation();

// Build-time crawl HTML is for non-JS bots only — hidden in index.html + `hidden`
// attr until this runs so refresh never flashes raw h1/p copy (GEO / FOUC).
document.getElementById('ss-crawl-content')?.remove();

ReactDOM.createRoot(document.getElementById('app')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>
);
